import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, RotateCcw, Trophy, ArrowLeft, Medal } from "lucide-react";
import { getFlagsGallery, getQuizLeaderboard, submitQuizScore } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { toast } from "sonner";

const shuffle = (a) => a.map((v) => [Math.random(), v]).sort((x, y) => x[0] - y[0]).map((p) => p[1]);
const BEST_KEY = "usapulse_flagquiz_best";

export default function FlagsQuiz() {
  const [flags, setFlags] = useState([]);
  const [q, setQ] = useState(null);       // { flag, options }
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(0);
  const [round, setRound] = useState(0);
  const [board, setBoard] = useState([]);
  const [initials, setInitials] = useState("");
  const [myRank, setMyRank] = useState(null);

  useEffect(() => { getFlagsGallery().then((d) => setFlags(d.flags)).catch(() => {}); }, []);
  useEffect(() => { getQuizLeaderboard().then((d) => setBoard(d.leaderboard)).catch(() => {}); }, []);
  useEffect(() => {
    const saved = parseInt(localStorage.getItem(BEST_KEY) || "0", 10);
    if (saved) setBest(saved);
  }, []);
  useEffect(() => {
    if (best > 0) localStorage.setItem(BEST_KEY, String(best));
  }, [best]);

  const submitScore = async () => {
    if (round === 0) { toast.info("Play a round first!"); return; }
    try {
      const res = await submitQuizScore({ name: initials || "USA", score, rounds: round });
      setBoard(res.leaderboard);
      setMyRank(res.rank);
      toast.success(`You ranked #${res.rank} on the leaderboard!`);
    } catch (_) { toast.error("Could not submit score"); }
  };

  const nextQuestion = useCallback(() => {
    if (flags.length < 4) return;
    const pool = shuffle(flags);
    const answer = pool[0];
    const distractors = pool.slice(1, 4);
    setQ({ flag: answer, options: shuffle([answer, ...distractors]) });
    setPicked(null);
    setRound((r) => r + 1);
  }, [flags]);

  useEffect(() => { if (flags.length >= 4 && !q) nextQuestion(); }, [flags, q, nextQuestion]);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt.geo_id === q.flag.geo_id) {
      const ns = streak + 1;
      setScore((s) => s + 1);
      setStreak(ns);
      setBest((b) => Math.max(b, ns));
    } else {
      setStreak(0);
    }
  };

  const restart = () => { setScore(0); setStreak(0); setRound(0); setQ(null); setPicked(null); };

  const share = async () => {
    const text = `I scored ${score}/${round} on the USAPulse Flags of America quiz! Can you beat me?`;
    const url = `${window.location.origin}/flags/quiz`;
    try {
      if (navigator.share) await navigator.share({ title: "USAPulse Flags Quiz", text, url });
      else { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Score copied — go share it!"); }
    } catch (_) {}
  };

  const accuracy = round > 0 ? Math.round((score / round) * 100) : 0;

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <Seo title="Flags of America Quiz — Guess the State Flag" path="/flags/quiz"
        description="Test your knowledge of U.S. state flags. Guess the state from its official flag and build your streak in this quick, shareable quiz." />

      <Link to="/flags" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-oldglory-red" data-testid="quiz-back">
        <ArrowLeft className="h-4 w-4" /> <T>Flags gallery</T>
      </Link>

      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-oldglory-red"><T>Flags of America</T></div>
          <h1 className="font-display text-3xl font-black tracking-tight text-navy sm:text-4xl"><T>Guess the state flag</T></h1>
        </div>
        <div className="flex gap-3 text-right">
          <div><div className="font-display text-2xl font-black text-navy" data-testid="quiz-score">{score}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground"><T>Score</T></div></div>
          <div><div className="font-display text-2xl font-black text-oldglory-red" data-testid="quiz-streak">{streak}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground"><T>Streak</T></div></div>
        </div>
      </div>

      {!q ? (
        <p className="animate-pulse font-display text-lg text-navy">Loading flags…</p>
      ) : (
        <div data-testid="quiz-card">
          <div className="mb-6 flex h-52 items-center justify-center rounded-2xl border border-border bg-parchment/40 p-4 shadow-inner">
            <AnimatePresence mode="wait">
              <motion.img
                key={q.flag.geo_id}
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                src={q.flag.flag_img} alt="Mystery state flag"
                className="max-h-full max-w-full object-contain shadow-md ring-1 ring-black/10"
                data-testid="quiz-flag-img"
              />
            </AnimatePresence>
          </div>

          <div className="grid grid-cols-2 gap-3" data-testid="quiz-options">
            {q.options.map((opt) => {
              const isAnswer = opt.geo_id === q.flag.geo_id;
              const isPicked = picked?.geo_id === opt.geo_id;
              let cls = "border-border bg-white hover:border-navy hover:bg-parchment";
              if (picked) {
                if (isAnswer) cls = "border-emerald-500 bg-emerald-50 text-emerald-800";
                else if (isPicked) cls = "border-red-400 bg-red-50 text-red-700";
                else cls = "border-border bg-white opacity-60";
              }
              return (
                <button key={opt.geo_id} onClick={() => choose(opt)} disabled={!!picked}
                  className={`flex items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left text-sm font-semibold text-navy transition-all ${cls}`}
                  data-testid={`quiz-option-${opt.geo_id}`}>
                  <span>{opt.name}</span>
                  {picked && isAnswer && <Check className="h-4 w-4 text-emerald-600" />}
                  {picked && isPicked && !isAnswer && <X className="h-4 w-4 text-red-500" />}
                </button>
              );
            })}
          </div>

          {picked && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">
                {picked.geo_id === q.flag.geo_id ? <T>Correct!</T> : <><T>It was</T> <Link to={`/place${q.flag.url_path}`} className="font-semibold text-oldglory-red hover:underline">{q.flag.name}</Link></>}
              </span>
              <button onClick={nextQuestion} className="rounded-full bg-navy px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy/90" data-testid="quiz-next">
                <T>Next flag</T> →
              </button>
            </motion.div>
          )}
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-border pt-5 text-sm">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Trophy className="h-4 w-4 text-amber-500" /> <T>Best streak</T>: <b className="text-navy">{best}</b></span>
        <span className="text-muted-foreground">· <T>Accuracy</T> {accuracy}%</span>
        <div className="ml-auto flex gap-2">
          <button onClick={share} className="rounded-full border border-navy/20 bg-white px-4 py-2 font-semibold text-navy hover:border-oldglory-red hover:text-oldglory-red" data-testid="quiz-share"><T>Share score</T></button>
          <button onClick={restart} className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 bg-white px-4 py-2 font-semibold text-navy hover:bg-parchment" data-testid="quiz-restart"><RotateCcw className="h-3.5 w-3.5" /> <T>Restart</T></button>
        </div>
      </div>

      <section className="mt-8 rounded-2xl border border-border bg-white p-5" data-testid="quiz-leaderboard">
        <div className="mb-4 flex items-center gap-2">
          <Medal className="h-5 w-5 text-amber-500" />
          <h2 className="font-display text-xl font-black text-navy"><T>Leaderboard</T></h2>
        </div>
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"><T>Your initials</T></span>
            <input value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 12))} placeholder="ABC" maxLength={12}
              className="w-28 rounded-lg border border-border px-3 py-2 text-sm font-bold uppercase tracking-wider text-navy outline-none focus:border-navy" data-testid="quiz-initials" />
          </label>
          <button onClick={submitScore} className="rounded-full bg-oldglory-red px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-oldglory-red/90" data-testid="quiz-submit-score">
            <T>Submit my score</T> ({score}/{round})
          </button>
        </div>
        {board.length === 0 ? (
          <p className="text-sm text-muted-foreground"><T>No scores yet — be the first!</T></p>
        ) : (
          <ol className="divide-y divide-border">
            {board.map((r, i) => (
              <li key={i} className={`flex items-center gap-3 py-2 text-sm ${myRank === i + 1 ? "font-bold text-oldglory-red" : "text-navy"}`} data-testid={`leaderboard-row-${i}`}>
                <span className="w-6 text-center font-display font-black text-muted-foreground">{i + 1}</span>
                <span className="w-16 font-bold uppercase tracking-wider">{r.name}</span>
                <span className="ml-auto font-display text-lg font-black">{r.score}</span>
                <span className="w-14 text-right text-xs text-muted-foreground">{r.accuracy}%</span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
